import { Cookies } from "commonAssets/cookies";
import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { createElement } from "commonAssets/lib/createElement";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { validateField } from "commonAssets/lib/validateField";
import { clearMessage } from "commonAssets/lib/clearMessage";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { loadMember } from "commonAssets/lib/loadMember";
import { uniformClaimDeadline } from "commonAssets/lib/uniformClaimDeadline";
import { loading, outputDiv } from "commonAssets/js/constants";
import { appPayMethod } from "commonAssets/js/constants";
import { dstMember } from "commonAssets/components/FormBase/DigitalDisbursements";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";

/**
 * Initialize the PURL cookie
 *
 * @param {Cookies} cookies - The cookies object
 * @param {number} cookieDuration - The duration of the cookie in days
 */
export function initPurl(cookies, cookieDuration = 0.5) {
    const params = new URLSearchParams(window.location.search);
    const uuid = params.get("uuid");

    if (uuid) {
        cookies.setCookie("_ag_purl", uuid, cookieDuration);
    }
}

/**
 * A class representing a set of methods for handling claim submissions via PURLs.
 * @class
 */
export class PurlSubmitClaim extends SubmitClaim {
    handle_error = (error) => {
        console.error(error);
        const e = new ErrorHandler();
        e.display_modal(error);
    };

    load_class_member = ({
        member,
        zero_zero_id,
        confirmation_code,
        form,
        container,
        claim_deadline,
    }) => {
        return this.claim.api
            .classMemberPost({}, member)
            .then((result) => {
                // Check for error message
                if (result.data.error) {
                    if (zero_zero_id) {
                        toggleFieldError(zero_zero_id, true, "");
                    }

                    if (confirmation_code) {
                        toggleFieldError(confirmation_code, true, "");
                    }

                    this.handle_error(result);
                    return;
                }

                // Add notice id and confirmation code to claim data
                this.claim.data.zero_zero_id = result.data.zero_zero_id;
                this.claim.data.confirmation_code =
                    result.data.confirmation_code;

                // Define the environment for the OTP
                const environment = this.claim.data.domain.split(".")[0]; // ada compliance - added

                if (result.data.claim_id) {
                    this.claim.data.claim_id = result.data.claim_id;
                    this.claim.class_member = result.data;
                    if (environment !== "local") {
                        this.send_otp(
                            form,
                            container,
                            result.data,
                            claim_deadline
                        );
                        return;
                    }
                }

                // Populate claim form
                this.populate_claim_form(
                    form,
                    container,
                    result.data,
                    claim_deadline
                );
            })
            .catch(this.handle_error);
    };

    load_class_member_from_purl({ purl, form, container, claim_deadline }) {
        return this.claim.api
            .classMemberPost(
                {},
                {
                    domain: this.claim.data.domain,
                    purl,
                }
            )
            .then(({ data }) => {
                if (data.error) {
                    return this.handle_error(data);
                }
                const member = {
                    domain: this.claim.data.domain,
                    zero_zero_id: data.zero_zero_id,
                    confirmation_code: data.confirmation_code,
                };
                return this.load_class_member({
                    member,
                    form,
                    container,
                    claim_deadline,
                });
            })
            .catch(this.handle_error);
    }

    init_claim(form) {
        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then(async (result) => {
                // Claim Deadline - Default Pacific DateTime
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let claim_deadline;
                if (Boolean(result.data.claim_deadline)) {
                    claim_deadline = result.data.claim_deadline;
                }

                // Error Handling
                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                /**
                 * Purl check start
                 */
                if (
                    document.cookie
                        .split(";")
                        .some((item) => item.trim().startsWith("_ag_purl="))
                ) {
                    const purl = document.cookie
                        .split(";")
                        .find((item) => item.trim().startsWith("_ag_purl="))
                        .split("=")[1];
                    return this.load_class_member_from_purl({
                        purl,
                        form,
                        container,
                        claim_deadline,
                    });
                }

                /**
                 * Claim Guard
                 */
                if (form.form_options.notice_confirmation.display === true) {
                    // Guard
                    container.appendChild(this.guard(form, datetime));
                    outputDiv.appendChild(container);

                    const zero_zero_id =
                        document.getElementById("zero_zero_id");
                    const confirmation_code =
                        document.getElementById("confirmation_code");
                    const skip_guard = document.getElementById("skip-guard");

                    // Notice Id and Confirmation Code
                    [zero_zero_id, confirmation_code].forEach(function (input) {
                        input.addEventListener("change", validateField);
                    });

                    // Skip Guard
                    if (skip_guard) {
                        skip_guard.addEventListener("click", () => {
                            // Hide guard
                            // document.getElementById('guard').classList.add("d-none");
                            document.getElementById("guard").remove();
                            // New Claim Form
                            container.appendChild(
                                this.claim_form(form, datetime)
                            );
                            container.scrollIntoView({ behavior: "smooth" });
                            outputDiv.appendChild(container);

                            // Init claim fields
                            this.init_claim_fields(
                                this.claim,
                                form,
                                claim_deadline
                            );
                        });
                    }

                    // ada compliance updates - added
                    // if input#submit-guard was clicked add .was-verified to form#guard
                    document
                        .getElementById("submit-guard")
                        .addEventListener("click", () => {
                            document
                                .getElementById("guard")
                                .classList.add("was-validated");
                            // TODO: add required text to labels
                        });

                    // Validate Guard Fields
                    document
                        .getElementById("guard")
                        .addEventListener("submit", (e) => {
                            e.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zero_zero_id.value.toUpperCase(),
                                confirmation_code: confirmation_code.value,
                            };

                            clearMessage();

                            // Validate guard fields
                            [zero_zero_id, confirmation_code].forEach(
                                function (input) {
                                    valid = validateField(input) && valid;
                                }
                            );
                            if (!valid) return;

                            loadingGif(loading, "show");

                            // Retrieve class member data (use POST to pass notice id and confirmation code privately)
                            this.load_class_member({
                                member,
                                zero_zero_id,
                                confirmation_code,
                                form,
                                container,
                                claim_deadline,
                            });
                        });
                } else {
                    /**
                     * No Claim Guard
                     */
                    // Edit claim link
                    this.edit_claim(form);

                    // New Claim Form
                    const deadlineSpan = uniformClaimDeadline(claim_deadline);
                    container.appendChild(this.claim_form(form, deadlineSpan));
                    outputDiv.appendChild(container);

                    // Init claim fields
                    this.init_claim_fields(this.claim, form, claim_deadline);
                }

                loadingGif(loading, "hide", 250);
            })
            .catch(this.handle_error);
    }

    populate_claim_form(form, container, class_member, claim_deadline) {
        console.log("[populate_claim_form]");
        // remove guard
        const guard = document.getElementById("guard");
        if (guard != null) {
            guard.remove();
        }

        // claim_deadline format December 31, 2024 at 04:00:00 P.M.
        const deadlineSpan = uniformClaimDeadline(claim_deadline);
        // New Claim Form
        container.appendChild(this.claim_form(form, deadlineSpan));
        container.scrollIntoView({ behavior: "smooth" });
        // output
        outputDiv.appendChild(container);
        // hide loading gif
        loadingGif(loading, "hide", 250);
        // Load member data into form
        loadMember(class_member);
        // custom login member code
        if (this.customLoadMember != null) {
            this.customLoadMember(this.claim);
        }
        // Payment widget
        if (appPayMethod && appPayMethod !== "") {
            dstMember();
        }
        // Init claim fields
        this.init_claim_fields(this.claim, form, claim_deadline);
    }
}
